import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { authLogin, authVerify2FA, authRegister, authForgot, authReset } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* --------------------- Sign in with Apple button --------------------- */
const AppleButton = () => {
  const [configured, setConfigured] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/auth/apple/status`)
      .then((r) => (r.ok ? r.json() : { configured: false }))
      .then((d) => { if (!cancelled) setConfigured(!!d.configured); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!configured) return null;
  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-zinc-800" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">or</span>
        <div className="flex-1 h-px bg-zinc-800" />
      </div>
      <a
        data-testid="apple-signin-button"
        href={`${BACKEND}/api/auth/apple/start`}
        className="flex items-center justify-center gap-2 w-full bg-white text-black font-bold text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-zinc-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
        </svg>
        Continue with Apple
      </a>
    </div>
  );
};

/* --------------------- shared shell --------------------- */
const AuthShell = ({ title, subtitle, testId, children, footerNote }) => (
  <div data-testid={testId} className="min-h-screen bg-black text-white relative overflow-hidden">
    <div className="brand-watermark" aria-hidden="true">
      <div className="wm wm-nc wm-1" /><div className="wm wm-clark wm-2" />
      <div className="wm wm-vip wm-3" /><div className="wm wm-clark wm-4" />
      <div className="wm wm-nc wm-5" /><div className="wm wm-vip wm-6" />
      <div className="wm wm-vip wm-7" /><div className="wm wm-nc wm-8" />
      <div className="wm wm-clark wm-9" />
    </div>
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="auth-back" className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 hover:text-white font-mono">
          ← Money/Hub
        </Link>
        <div className="mt-8 mb-3 flex items-center gap-3 justify-center opacity-80">
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/0086jwtl_NC%20Const.Logo%20Editable%20Jul%2017%2C%202026%2C%2012_37_50%20AM.png" alt="NC Construction" className="h-10 w-auto" />
          <div className="w-px h-8 bg-zinc-800" />
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/c2mpfljt_image.png" alt="Clark Properties" className="h-10 w-auto" />
          <div className="w-px h-8 bg-zinc-800" />
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/7fk0o84l_MoneyHub%20LogoBest%20Image%20Jul%2017%2C%202026%2C%2012_33_13%20AM.png" alt="MoneyHub VIP" className="h-10 w-auto" />
        </div>
        <h1 className="font-heading text-4xl font-black text-center mb-2">{title}</h1>
        {subtitle && <p className="text-zinc-300 text-center text-sm mb-8 font-mono">{subtitle}</p>}
        <div className="bg-zinc-950/90 border border-zinc-800 p-6 backdrop-blur-sm">
          {children}
        </div>
        {footerNote && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-mono">
            {footerNote}
          </div>
        )}
      </div>
    </div>
  </div>
);

const Field = ({ label, testId, type = "text", value, onChange, placeholder, autoComplete }) => (
  <label className="block mb-4">
    <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mb-2">{label}</span>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="w-full bg-black border border-zinc-800 focus:border-[#60a5fa] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-sm px-3 py-3"
    />
  </label>
);

const Submit = ({ testId, children, disabled }) => (
  <button
    data-testid={testId}
    type="submit"
    disabled={disabled}
    className="w-full mt-2 bg-[#60a5fa] text-black font-bold text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#93c5fd] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
  >
    {children}
  </button>
);

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return String(d);
};

/* --------------------- Login --------------------- */
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Seed challengeId from ?owner_2fa_challenge=... so Google-login owner 2FA lands on this form.
  const _urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const [challengeId, setChallengeId] = useState(_urlParams?.get("owner_2fa_challenge") || null);
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authLogin(email, password);
      if (r?.requires_2fa) {
        setChallengeId(r.challenge_id);
        toast.success("6-digit code sent to your email");
        return;
      }
      toast.success("Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authVerify2FA(challengeId, code.trim());
      toast.success("Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  if (challengeId) {
    return (
      <AuthShell
        testId="login-2fa-page"
        title="Owner verification"
        subtitle="// 6-digit code sent to your email · expires in 10 minutes"
        footerNote={
          <button
            type="button"
            data-testid="2fa-cancel"
            onClick={() => { setChallengeId(null); setCode(""); }}
            className="text-[#60a5fa] hover:text-white text-[10px] tracking-[0.3em] uppercase font-mono"
          >
            ← Cancel and use a different account
          </button>
        }
      >
        <form onSubmit={submit2FA}>
          <Field
            label="Verification code"
            testId="2fa-code"
            type="text"
            value={code}
            onChange={setCode}
            placeholder="123456"
            autoComplete="one-time-code"
          />
          <Submit testId="2fa-submit" disabled={busy || code.length < 4}>
            {busy ? "Verifying…" : "Verify & sign in →"}
          </Submit>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testId="login-page"
      title="Sign in"
      subtitle="// email + password · works with any address"
      footerNote={<>New here? <Link to="/register" data-testid="link-register" className="text-[#60a5fa] hover:text-white">Create an account →</Link></>}
    >
      <form onSubmit={submit}>
        <Field label="Email" testId="login-email" type="email" value={email} onChange={setEmail} placeholder="you@anywhere.com" autoComplete="email" />
        <Field label="Password" testId="login-password" type="password" value={password} onChange={setPassword} placeholder="••••••••" autoComplete="current-password" />
        <Submit testId="login-submit" disabled={busy || !email || !password}>
          {busy ? "Signing in…" : "Sign in →"}
        </Submit>
        <div className="mt-4 flex justify-between text-[10px] tracking-[0.3em] uppercase font-mono">
          <Link to="/forgot-password" data-testid="link-forgot" className="text-zinc-300 hover:text-white">Forgot password?</Link>
          <Link to="/" data-testid="link-google" className="text-zinc-300 hover:text-white">Use Google instead →</Link>
        </div>
        <AppleButton />
      </form>
    </AuthShell>
  );
}

/* --------------------- Register --------------------- */
export function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authRegister(email, password, name);
      toast.success(r.message || "Account created");
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <AuthShell testId="register-sent" title="Check your inbox" subtitle="// verification link sent">
        <p className="text-zinc-200 text-sm leading-relaxed">
          We sent a verification link to <b className="text-white">{email}</b>. Click it to activate your account and jump into the dashboard.
        </p>
        <p className="text-zinc-300 text-xs font-mono mt-4">// Didn&apos;t get it? Check spam, then re-register with the same email.</p>
        <div className="mt-6 text-center">
          <Link to="/login" data-testid="link-login-after-register" className="text-[#60a5fa] text-[10px] tracking-[0.3em] uppercase font-mono">Back to login →</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testId="register-page"
      title="Create account"
      subtitle="// email + password · any address"
      footerNote={<>Already have an account? <Link to="/login" data-testid="link-login" className="text-[#60a5fa] hover:text-white">Sign in →</Link></>}
    >
      <form onSubmit={submit}>
        <Field label="Full name (optional)" testId="register-name" value={name} onChange={setName} placeholder="Nick Clark" autoComplete="name" />
        <Field label="Email" testId="register-email" type="email" value={email} onChange={setEmail} placeholder="you@anywhere.com" autoComplete="email" />
        <Field label="Password" testId="register-password" type="password" value={password} onChange={setPassword} placeholder="min 8 chars · letter + number" autoComplete="new-password" />
        <div className="text-[10px] font-mono text-zinc-300 -mt-2 mb-4 leading-relaxed">
          // At least 8 characters, includes a letter and a number.
        </div>
        <Submit testId="register-submit" disabled={busy || !email || !password}>
          {busy ? "Creating…" : "Create my account →"}
        </Submit>
      </form>
    </AuthShell>
  );
}

/* --------------------- Forgot password --------------------- */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authForgot(email);
      toast.success(r.message);
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell
      testId="forgot-page"
      title="Reset password"
      subtitle="// we'll email you a reset link"
      footerNote={<Link to="/login" data-testid="link-back-login" className="text-[#60a5fa] hover:text-white">← Back to sign in</Link>}
    >
      {sent ? (
        <p className="text-zinc-200 text-sm leading-relaxed">
          If an account exists for <b className="text-white">{email}</b>, a reset link is on its way. Link expires in 1 hour.
        </p>
      ) : (
        <form onSubmit={submit}>
          <Field label="Email" testId="forgot-email" type="email" value={email} onChange={setEmail} placeholder="you@anywhere.com" autoComplete="email" />
          <Submit testId="forgot-submit" disabled={busy || !email}>
            {busy ? "Sending…" : "Email me a reset link →"}
          </Submit>
        </form>
      )}
    </AuthShell>
  );
}

/* --------------------- Reset password --------------------- */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { setToken(params.get("token") || ""); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authReset(token, password);
      toast.success(r.message);
      setDone(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell testId="reset-page" title="Set new password" subtitle="// min 8 chars · letter + number">
      {done ? (
        <>
          <p className="text-[#22c55e] text-sm font-mono">✓ Password reset. Sign in with your new password.</p>
          <div className="mt-6 text-center">
            <Link to="/login" data-testid="link-login-after-reset" className="text-[#60a5fa] text-[10px] tracking-[0.3em] uppercase font-mono">Sign in →</Link>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <Field label="New password" testId="reset-password" type="password" value={password} onChange={setPassword} placeholder="new password" autoComplete="new-password" />
          <Submit testId="reset-submit" disabled={busy || !token || password.length < 8}>
            {busy ? "Saving…" : "Save new password →"}
          </Submit>
          {!token && <div className="mt-3 text-[10px] font-mono text-[#fb7185]">// No token in the URL — check the reset email link you were sent.</div>}
        </form>
      )}
    </AuthShell>
  );
}
